import { useState, useEffect } from 'react';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import {
  RESOURCES_FILTERS_ID,
  RESOURCES_STICKY_TOP_CLASS,
  isResourceCategorySlug,
  scheduleScrollToResourcesFilters,
} from '../constants/resources';
import { FileText, Download, Filter, Search, Book, Scale, BarChart, GraduationCap, X } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const categories = [
  { value: 'all', label: 'All Categories', icon: FileText },
  { value: 'planning', label: 'Planning Guidance', icon: Scale },
  { value: 'policy', label: 'Policy Documents', icon: Book },
  { value: 'reports', label: 'Reports & Research', icon: BarChart },
  { value: 'education', label: 'Educational Materials', icon: GraduationCap },
];

const resources = [
  {
    id: '1',
    title: 'Planning Objection Guide',
    description: 'A comprehensive guide to writing effective planning objections, with templates and examples.',
    category: 'planning',
    type: 'PDF',
    size: '2.4 MB',
    downloadUrl: '#',
    date: '2025-12-01',
  },
  {
    id: '2',
    title: 'Annual Report 2025',
    description: 'Our annual report covering conservation achievements, financial overview, and future plans.',
    category: 'reports',
    type: 'PDF',
    size: '8.1 MB',
    downloadUrl: '#',
    date: '2025-11-15',
  },
  {
    id: '3',
    title: 'Countryside Policy Framework',
    description: 'CPRE\'s policy positions on development, green belt protection, and sustainable agriculture.',
    category: 'policy',
    type: 'PDF',
    size: '1.8 MB',
    downloadUrl: '#',
    date: '2025-10-20',
  },
  {
    id: '4',
    title: 'Wildlife Survey Handbook',
    description: 'Learn how to conduct wildlife surveys and contribute to our citizen science programs.',
    category: 'education',
    type: 'PDF',
    size: '3.2 MB',
    downloadUrl: '#',
    date: '2025-09-15',
  },
  {
    id: '5',
    title: 'Green Belt Protection Toolkit',
    description: 'Essential resources for campaigning to protect green belt land in your community.',
    category: 'planning',
    type: 'PDF',
    size: '4.5 MB',
    downloadUrl: '#',
    date: '2025-08-10',
  },
  {
    id: '6',
    title: 'State of the Countryside Report',
    description: 'Analysis of environmental trends, development pressures, and conservation opportunities.',
    category: 'reports',
    type: 'PDF',
    size: '12.3 MB',
    downloadUrl: '#',
    date: '2025-07-01',
  },
  {
    id: '7',
    title: 'Teacher\'s Countryside Pack',
    description: 'Educational resources for schools about countryside conservation and environmental stewardship.',
    category: 'education',
    type: 'ZIP',
    size: '45.6 MB',
    downloadUrl: '#',
    date: '2025-06-15',
  },
  {
    id: '8',
    title: 'Climate Action Policy',
    description: 'Our policy framework for addressing climate change through countryside management.',
    category: 'policy',
    type: 'PDF',
    size: '2.1 MB',
    downloadUrl: '#',
    date: '2025-05-20',
  },
];

const categoryIcons = {
  planning: Scale,
  policy: Book,
  reports: BarChart,
  education: GraduationCap,
};

const categoryColors = {
  planning: 'bg-blue-500 text-white',
  policy: 'bg-purple-500 text-white',
  reports: 'bg-countryside-green text-white',
  education: 'bg-amber-500 text-white',
};

const ResourcesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [search, setSearch] = useState('');
  
  const categoryFilter = searchParams.get('category') || 'all';

  useEffect(() => {
    if (!isResourceCategorySlug(categoryFilter)) return;
    return scheduleScrollToResourcesFilters({ delayMs: 50 });
  }, [categoryFilter, location.pathname, location.search]);

  const updateFilter = (value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value === 'all') {
      newParams.delete('category');
    } else {
      newParams.set('category', value);
    }
    setSearchParams(newParams);
  };

  const filteredResources = resources.filter(r => {
    const matchesCategory = categoryFilter === 'all' || r.category === categoryFilter;
    const matchesSearch = search === '' || 
      r.title.toLowerCase().includes(search.toLowerCase()) ||
      r.description.toLowerCase().includes(search.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <Layout>
      <div data-testid="resources-page">
        {/* Hero */}
        <section className="relative py-20 md:py-28 overflow-hidden bg-countryside-green">
          <div className="absolute inset-0 opacity-20">
            <img
              src="/images/cpre images for website/2.jpg"
              alt="Resources"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="relative container-custom text-center text-white">
            <Badge className="bg-cpre-harvest-gold text-cpre-new-yew hover:bg-cpre-autumn-leaf hover:text-white mb-6 inline-flex items-center gap-2 rounded-full text-sm font-medium px-4 py-2">
              <FileText className="w-4 h-4" />
              Knowledge Hub
            </Badge>
            <h1 className="text-4xl sm:text-5xl font-heading font-bold mb-4 text-white">Resource Library</h1>
            <p className="text-xl text-white/90 max-w-2xl mx-auto">
              Access planning guides, policy documents, research reports, and educational materials.
            </p>
          </div>
        </section>

        {/* Filters */}
        <div
          id={RESOURCES_FILTERS_ID}
          className="scroll-mt-[6.5rem] md:scroll-mt-[7.5rem]"
        >
          <div
            className={`sticky ${RESOURCES_STICKY_TOP_CLASS} z-40 bg-white border-b border-stone-200 shadow-sm`}
          >
            <div className="container-custom py-4 md:py-5">
              <div
                className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_18rem] gap-4 xl:gap-6 xl:items-center"
                role="toolbar"
                aria-label="Filter resources"
              >
              <div className="flex flex-wrap gap-2 min-w-0">
                {categories.map((cat) => (
                  <Button
                    key={cat.value}
                    variant={categoryFilter === cat.value ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => updateFilter(cat.value)}
                    className={`shrink-0 whitespace-nowrap rounded-full ${
                      categoryFilter === cat.value
                        ? 'bg-countryside-green hover:bg-countryside-green-dark'
                        : 'border-stone-300'
                    }`}
                    data-testid={`filter-${cat.value}`}
                  >
                    <cat.icon className="w-4 h-4 mr-2 shrink-0" />
                    {cat.label}
                  </Button>
                ))}
              </div>

              <div className="relative w-full min-w-0 xl:max-w-xs xl:justify-self-end">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <Input
                  placeholder="Search resources..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 rounded-lg w-full"
                  data-testid="search-resources"
                />
              </div>
            </div>
          </div>
        </div>
        </div>

        {/* Resources Grid */}
        <section className="section-padding pt-8 md:pt-10">
          <div className="container-custom">
            {filteredResources.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-muted-foreground text-lg">No resources found matching your criteria.</p>
                <Button 
                  onClick={() => { setSearch(''); updateFilter('all'); }} 
                  className="mt-4" 
                  variant="outline"
                >
                  Clear Filters
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredResources.map((resource, index) => {
                  const Icon = categoryIcons[resource.category] || FileText;
                  const colorClass = categoryColors[resource.category] || 'bg-stone-500 text-white';

                  return (
                    <Card 
                      key={resource.id}
                      className="bg-white border-stone-200 hover:shadow-lg transition-shadow duration-300 group"
                      data-testid={`resource-card-${index}`}
                    >
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className={`w-12 h-12 ${colorClass.split(' ')[0]} rounded-xl flex items-center justify-center`}>
                            <Icon className="w-6 h-6 text-white" />
                          </div>
                          <Badge variant="outline" className="text-xs">
                            {resource.type} • {resource.size}
                          </Badge>
                        </div>

                        <h3 className="font-heading font-semibold text-lg mb-2 group-hover:text-countryside-green transition-colors duration-200">
                          {resource.title}
                        </h3>
                        <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
                          {resource.description}
                        </p>

                        <div className="flex items-center justify-between pt-4 border-t border-stone-100">
                          <span className="text-xs text-muted-foreground">
                            {formatDate(resource.date)}
                          </span>
                          <Button 
                            type="button"
                            size="sm" 
                            className="bg-countryside-green hover:bg-countryside-green-dark text-white rounded-lg"
                            data-testid={`download-${index}`}
                            onClick={() => toast.info('Coming Soon', {
                              description: 'Downloads will be available here soon.',
                            })}
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Download
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {/* Help Section */}
        <section className="py-16 bg-countryside-bone/30">
          <div className="container-custom text-center">
            <h2 className="text-2xl font-heading font-bold mb-4">Can't Find What You Need?</h2>
            <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
              If you're looking for specific information or documents, get in touch with our team.
            </p>
            <Link to="/about#contact">
              <Button className="bg-countryside-green hover:bg-countryside-green-dark text-white rounded-full px-8">
                Contact Us
              </Button>
            </Link>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default ResourcesPage;
